import Timer from "./Timer";
import "./VoiceWaveform.css";
import { useEventListener } from "ahooks";
import React, { memo, useMemo } from "react";

/**
 * @name VoiceWaveformProps 声波可视化组件 Props
 * @description VoiceWaveform 组件的属性接口定义
 */
interface VoiceWaveformProps {
  /**
   * @name audioData 音频频率数据
   * @description 来自 Web Audio API AnalyserNode 的频率数据数组 (0-255)
   */
  audioData: Uint8Array | null;
  /**
   * @name onStop 停止录音回调
   * @description 用户点击停止按钮时调用
   */
  onStop?: () => void;
  /**
   * @name barCount 条形数量
   * @description 显示的声波条形数量，默认 50
   */
  barCount?: number;
  /**
   * @name isLoading 是否正在上传
   * @description 上传中时停止按钮显示 loading 状态
   */
  isLoading?: boolean;
  /**
   * @name className 自定义类名
   * @description 可选的额外 CSS 类名
   */
  className?: string;
}

/**
 * @name generateBellCurveWeights 生成钟形曲线权重
 * @param count 数量
 * @description 生成一组钟形曲线权重，使中间的条形更高
 */
function generateBellCurveWeights(count: number): number[] {
  const weights: number[] = [];
  const center = (count - 1) / 2;
  const sigma = count / 4; // 标准差控制曲线宽度

  for (let i = 0; i < count; i++) {
    // 高斯分布公式：e^(-(x-μ)²/(2σ²))
    const x = i - center;
    const weight = Math.exp(-(x * x) / (2 * sigma * sigma));
    // 将权重范围调整到 0.3 - 1.0，避免边缘完全平坦
    weights.push(0.3 + weight * 0.7);
  }

  return weights;
}

/**
 * @name VoiceWaveform 声波可视化组件
 * @description 显示实时音频声波动画，随声音大小波动
 *
 * 功能特点：
 * - 显示多个垂直条形，高度随音频数据变化
 * - 中间较高，两边较低的钟形曲线分布
 * - 包含停止录音按钮
 * - 支持桌面端和移动端响应式布局
 */
const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
  audioData,
  onStop,
  barCount = 60,
  isLoading = false,
  className = "",
}) => {
  // 生成钟形曲线权重（只计算一次）
  const bellCurveWeights = useMemo(
    () => generateBellCurveWeights(barCount),
    [barCount]
  );

  // 计算每个条形的高度
  const barHeights = useMemo(() => {
    const heights: number[] = [];

    if (!audioData || audioData.length === 0) {
      // 无数据时显示最小高度
      for (let i = 0; i < barCount; i++) {
        heights.push(4);
      }
      return heights;
    }

    // 将音频数据映射到条形数量
    const step = audioData.length / barCount;

    for (let i = 0; i < barCount; i++) {
      // 获取对应位置的音频数据（取平均值以平滑效果）
      const startIndex = Math.floor(i * step);
      const endIndex = Math.min(Math.floor((i + 1) * step), audioData.length);

      let sum = 0;
      let count = 0;
      for (let j = startIndex; j < endIndex; j++) {
        sum += audioData[j];
        count++;
      }

      // 计算平均值并应用钟形曲线权重
      const avgValue = count > 0 ? sum / count : 0;
      const normalizedValue = avgValue / 255; // 归一化到 0-1
      const weightedValue = normalizedValue * bellCurveWeights[i];

      // 计算高度（最小 4px，最大 40px）
      const minHeight = 4;
      const maxHeight = 40;
      const height = minHeight + weightedValue * (maxHeight - minHeight);

      heights.push(height);
    }

    return heights;
  }, [audioData, barCount, bellCurveWeights]);

  useEventListener('visibilitychange', () => {
    // console.log('@-切换', document.hidden);
    // 若切换了标签页，就停止渲染音频波纹
    document.hidden && onStop();
  });

  return (
    <div className={`voice-waveform ${className}`}>
      {/* 声波条形容器 */}
      <div className="voice-waveform__bars">
        {barHeights.map((height, index) => (
          <div
            key={index}
            className="voice-waveform__bar"
            style={{
              height: `${height}px`,
            }}
          />
        ))}
      </div>

    <div className="voice-waveform-controller">
      {isLoading ? null : <Timer seconds={60} />}
     {/* 停止按钮 / Loading */}
      <button
        type="button"
        className={`voice-waveform__stop-btn ${isLoading ? 'voice-waveform__stop-btn--loading' : ''}`}
        onClick={isLoading ? undefined : onStop}
        aria-label={isLoading ? "Uploading" : "Stop recording"}
        disabled={isLoading}
      >
        {isLoading ? (
          // Loading 状态显示转圈
          <div className="voice-waveform__loading-spinner" />
        ) : (
          <>
            {/* 录音指示器（红点闪烁动画） David 说不要这个 */}
            {/* <span className="voice-waveform__recording-indicator" /> */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </>
        )}
      </button>

    </div>
    </div>
  );
};

export default memo(VoiceWaveform);
